import {
  faUsers, faComments, faWrench, faClock, faExclamationTriangle, faChartLine, faBullseye,
  faGraduationCap, faEnvelope, faTrafficLight, faCreditCard, faCloud, faCalendarAlt, faLink,
  faBell, faCircleQuestion,
} from '@/fontawesome';
import { MailStatus, DiscordMessageStatus, TelegramMessageStatus, SCNMessageStatus, PushNotificationStatus, StocksStatusLightColor } from '@/models';
import type { StocksStatusLight } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import { StatCard, StatNumber, StatValue, useAsyncValue } from './StatCard';

/** "pending / total" as used by all the queue cards */
function QueueValue({ pending, total }: { pending: number; total: number }) {
  return (
    <p className="text-3xl font-bold text-gray-900 dark:text-white">
      {pending} / {total}
    </p>
  );
}

export default function AdminDashboard() {
  // One request (or small group) per card — cards fill in as their own data arrives instead
  // of the whole page waiting for the slowest endpoint.
  const users = useAsyncValue(() => APIRoutes.listUsers().then(r => r.total));
  const chatSessions = useAsyncValue(() => APIRoutes.listAllChatSessions().then(r => r.total));
  const tools = useAsyncValue(() => APIRoutes.listTools().then(r => r.tools.length));
  const jobs = useAsyncValue(() => APIRoutes.listJobs().then(r => r.jobs.length));
  const symbols = useAsyncValue(() => APIRoutes.listSymbols({ limit: 1 }).then(r => r.total));
  const courses = useAsyncValue(() => APIRoutes.listCourses({ limit: 1 }).then(r => r.total));
  const subscriptions = useAsyncValue(() => APIRoutes.listAllSubscriptions({ limit: 1 }).then(r => r.total));
  const events = useAsyncValue(() => APIRoutes.listEvents({ pageSize: 1 }).then(r => r.total));
  const errors = useAsyncValue(() => APIRoutes.listErrors({ acknowledged: false, severity: 'Err,Fatal', limit: 1 }).then(r => r.total));
  const statusLight = useAsyncValue<StocksStatusLight | null>(() => APIRoutes.getLatestStocksStatusLight().catch(() => null));

  const recommendations = useAsyncValue(async () => {
    const [core, earnings, swing, activeCore, activeEarnings, activeSwing] = await Promise.all([
      APIRoutes.listCoreRecommendations({ limit: 1 }).then(r => r.total),
      APIRoutes.listEarningsRecommendations({ limit: 1 }).then(r => r.total),
      APIRoutes.listSwingTrades({ limit: 1 }).then(r => r.total),
      APIRoutes.listCoreRecommendations({ limit: 1, state: ['OPEN', 'ASSIGNED'] }).then(r => r.total),
      APIRoutes.listEarningsRecommendations({ limit: 1, state: ['OPEN', 'ASSIGNED'] }).then(r => r.total),
      APIRoutes.listSwingTrades({ limit: 1, state: 'OPEN' }).then(r => r.total),
    ]);
    return { total: core + earnings + swing, active: activeCore + activeEarnings + activeSwing };
  });

  const mails = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listMails({ limit: 1 }).then(r => r.total),
      APIRoutes.listMails({ limit: 1, status: MailStatus.WAITING }).then(r => r.total),
    ]);
    return { total, pending };
  });

  const discord = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listDiscordMessages({ limit: 1 }).then(r => r.total),
      APIRoutes.listDiscordMessages({ limit: 1, status: DiscordMessageStatus.WAITING }).then(r => r.total),
    ]);
    return { total, pending };
  });

  const telegram = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listTelegramMessages({ limit: 1 }).then(r => r.total),
      APIRoutes.listTelegramMessages({ limit: 1, status: TelegramMessageStatus.WAITING }).then(r => r.total),
    ]);
    return { total, pending };
  });

  const scn = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listSCNMessages({ limit: 1 }).then(r => r.total),
      APIRoutes.listSCNMessages({ limit: 1, status: SCNMessageStatus.WAITING }).then(r => r.total),
    ]);
    return { total, pending };
  });

  const pushNotifications = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listPushNotifications({ limit: 1 }).then(r => r.total),
      APIRoutes.listPushNotifications({ limit: 1, status: PushNotificationStatus.WAITING }).then(r => r.total),
    ]);
    return { total, pending };
  });

  const questions = useAsyncValue(async () => {
    const [total, pending] = await Promise.all([
      APIRoutes.listUserQuestions({ limit: 1 }).then(r => r.total),
      APIRoutes.listUserQuestions({ limit: 1, status: 'OPEN' }).then(r => r.total),
    ]);
    return { total, pending };
  });

  return (
    <>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        Admin Dashboard
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard to="/admin/users" label="Aktuelle Benutzer" icon={faUsers} accent="blue">
          <StatNumber state={users} />
        </StatCard>

        <StatCard to="/admin/sessions" label="Chat-Sitzungen" icon={faComments} accent="green">
          <StatNumber state={chatSessions} />
        </StatCard>

        <StatCard to="/admin/tools" label="Verfügbare Tools" icon={faWrench} accent="orange">
          <StatNumber state={tools} />
        </StatCard>

        <StatCard to="/admin/symbols" label="Trading Symbole" icon={faChartLine} accent="cyan">
          <StatNumber state={symbols} />
        </StatCard>

        <StatCard to="/admin/jobs" label="Jobs" icon={faClock} accent="purple">
          <StatNumber state={jobs} />
        </StatCard>

        <StatCard to="/admin/trading-recommendations" label="Trading Tradingideen" icon={faBullseye} accent="amber">
          <StatValue state={recommendations}>
            {v => (
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {v.active} / {v.total}
              </p>
            )}
          </StatValue>
        </StatCard>

        <StatCard to="/admin/courses" label="Kurse" icon={faGraduationCap} accent="indigo">
          <StatNumber state={courses} />
        </StatCard>

        <StatCard to="/admin/mails" label="E-Mails" icon={faEnvelope} accent="teal">
          <StatValue state={mails}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/discord-messages" label="Discord-Nachrichten" icon={faComments} accent="purple">
          <StatValue state={discord}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/telegram-messages" label="Telegram-Nachrichten" icon={faComments} accent="sky">
          <StatValue state={telegram}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/scn-messages" label="SCN-Nachrichten" icon={faCloud} accent="green">
          <StatValue state={scn}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/push-notifications" label="Push-Benachrichtigungen" icon={faBell} accent="purple">
          <StatValue state={pushNotifications}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/questions" label="Fragen" icon={faCircleQuestion} accent="blue">
          <StatValue state={questions}>{v => <QueueValue pending={v.pending} total={v.total} />}</StatValue>
        </StatCard>

        <StatCard to="/admin/stocks-status-lights" label="Börsen-Ampel" icon={faTrafficLight} accent="amber">
          <StatValue state={statusLight} skeletonClassName="h-8 w-28">
            {light => light === null ? (
              <p className="text-xl font-bold text-gray-500 dark:text-gray-400">Nicht gesetzt</p>
            ) : (
              <>
                <div className="flex items-center space-x-2 mb-1">
                  <div
                    className={`w-4 h-4 rounded-full ${
                      light.color === StocksStatusLightColor.GREEN
                        ? 'bg-green-500'
                        : light.color === StocksStatusLightColor.YELLOW
                        ? 'bg-yellow-400'
                        : 'bg-red-500'
                    }`}
                  />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {light.color === StocksStatusLightColor.GREEN
                      ? 'Grün'
                      : light.color === StocksStatusLightColor.YELLOW
                      ? 'Gelb'
                      : 'Rot'}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{light.comment}</p>
              </>
            )}
          </StatValue>
        </StatCard>

        <StatCard to="/admin/subscriptions" label="Subscriptions" icon={faCreditCard} accent="emerald">
          <StatNumber state={subscriptions} />
        </StatCard>

        <StatCard to="/admin/registrierungs-links" label="Registrierungs-Links" icon={faLink} accent="rose">
          <p className="text-xl font-bold text-gray-900 dark:text-white">Generator</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Link mit Paket &amp; Coupon</p>
        </StatCard>

        <StatCard to="/admin/events" label="Events" icon={faCalendarAlt} accent="purple">
          <StatNumber state={events} />
        </StatCard>

        <StatCard to="/admin/errors" label="Unbearbeitete Fehler" icon={faExclamationTriangle} accent="red">
          <StatNumber state={errors} />
        </StatCard>
      </div>
    </>
  );
}
